import React, { useEffect, useState } from "react";
import Moment from "react-moment";
import UploadDocPopup from "../UploadDocPopup";
import { useDispatch, useSelector } from "react-redux";
import { getAppDocuments, getAppTasks, getCandidateApps, loadAppDocuments, loadAppRuningTasks, loadCandidateApps } from "../../app/store/candidate";
import { getCurrentUser, getLoggedUserDetails, loadUserDetails } from "../../app/store/auth";
import { getAlert } from "../../Services/appAlerts";
import { setCurrentPageTitle, setCurrentRoute } from "../../app/store/layoutUI";
import useDocDownload from "../../app/hooks/useDocDownload";
import { getComponentUserStatus, UpdateComponentsUserStatus } from "../../app/store/tools";
import { getInvDocuments, getReqDocuments, loadInvoiceDocuments, loadReqDocuments } from "../../app/store/documents";

const SENT_DOCS_STYLES = {
  pageWrap: {
    padding: "12px 0 4px 0"
  },
  shell: {
    borderRadius: "24px",
    border: "1px solid rgba(226, 232, 240, 1)",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
    boxShadow: "0 18px 38px rgba(15, 23, 42, 0.06)",
    overflow: "hidden"
  },
  hero: {
    padding: "24px 24px 18px 24px",
    borderBottom: "1px solid rgba(226, 232, 240, 0.9)",
    background: "linear-gradient(135deg, rgba(248, 250, 252, 0.98) 0%, rgba(239, 246, 255, 0.95) 100%)"
  },
  heroRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
    flexWrap: "wrap"
  },
  titleWrap: {
    minWidth: 0,
    display: "flex",
    alignItems: "flex-start",
    gap: "14px"
  },
  titleIcon: {
    width: "52px",
    height: "52px",
    borderRadius: "16px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, rgba(37, 99, 235, 0.12) 0%, rgba(79, 70, 229, 0.16) 100%)",
    color: "#4338ca",
    fontSize: "24px",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)"
  },
  titleCopy: {
    minWidth: 0
  },
  eyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "11px",
    fontWeight: 800,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#4f46e5",
    marginBottom: "10px"
  },
  title: {
    fontSize: "24px",
    fontWeight: 800,
    lineHeight: 1.15,
    color: "#0f172a",
    margin: 0
  },
  subtitle: {
    fontSize: "14px",
    lineHeight: 1.6,
    color: "#475569",
    margin: "8px 0 0 0",
  },
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "12px",
    marginTop: "18px"
  },
  statCard: {
    borderRadius: "18px",
    padding: "14px 16px",
    background: "#ffffff",
    border: "1px solid rgba(191, 219, 254, 0.9)",
    boxShadow: "0 10px 26px rgba(37, 99, 235, 0.06)"
  },
  statLabel: {
    fontSize: "11px",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#64748b",
    marginBottom: "6px"
  },
  statValue: {
    fontSize: "26px",
    fontWeight: 800,
    color: "#0f172a",
    lineHeight: 1
  },
  statHint: {
    fontSize: "12px",
    lineHeight: 1.5,
    color: "#64748b",
    marginTop: "6px"
  },
  body: {
    padding: "20px 24px 24px 24px",
    display: "grid",
    gap: "20px"
  },
  sectionCard: {
    borderRadius: "20px",
    border: "1px solid rgba(226, 232, 240, 1)",
    background: "#ffffff",
    overflow: "hidden"
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    padding: "16px 18px",
    borderBottom: "1px solid rgba(226, 232, 240, 0.9)",
    background: "linear-gradient(180deg, rgba(248, 250, 252, 0.98) 0%, rgba(255, 255, 255, 1) 100%)"
  },
  sectionTitleWrap: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    minWidth: 0
  },
  sectionIcon: {
    width: "38px",
    height: "38px",
    borderRadius: "12px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(79, 70, 229, 0.08)",
    color: "#4338ca",
    fontSize: "18px"
  },
  sectionTitle: {
    fontSize: "17px",
    fontWeight: 800,
    color: "#0f172a"
  },
  sectionMeta: {
    fontSize: "12px",
    color: "#64748b",
    marginTop: "2px"
  },
  sectionBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "34px",
    height: "34px",
    padding: "0 12px",
    borderRadius: "999px",
    background: "rgba(37, 99, 235, 0.08)",
    color: "#1d4ed8",
    fontSize: "12px",
    fontWeight: 800
  },
  table: {
    marginBottom: 0
  },
  tableHeadCell: {
    borderTop: "none",
    fontSize: "12px",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#64748b",
    background: "#f8fafc",
    padding: "14px 16px"
  },
  cell: {
    verticalAlign: "middle",
    padding: "16px"
  },
  serialBadge: {
    minWidth: "34px",
    height: "34px",
    borderRadius: "999px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(79, 70, 229, 0.08)",
    color: "#4338ca",
    fontSize: "13px",
    fontWeight: 800
  },
  dateText: {
    fontSize: "13px",
    fontWeight: 700,
    color: "#0f172a"
  },
  fileButton: {
    border: "none",
    background: "transparent",
    padding: 0,
    color: "#1d4ed8",
    fontSize: "13px",
    fontWeight: 800,
    textAlign: "left"
  },
  fileSubtext: {
    fontSize: "12px",
    color: "#64748b",
    marginTop: "4px"
  },
  typeBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: "999px",
    background: "rgba(37, 99, 235, 0.08)",
    color: "#1d4ed8",
    fontSize: "11px",
    fontWeight: 800
  },
  notesText: {
    fontSize: "12px",
    lineHeight: 1.6,
    color: "#475569",
    whiteSpace: "pre-wrap"
  },
  emptyState: {
    padding: "42px 20px",
    textAlign: "center",
    background: "linear-gradient(180deg, rgba(248,250,252,0.8) 0%, rgba(255,255,255,1) 100%)"
  },
  emptyIcon: {
    width: "64px",
    height: "64px",
    borderRadius: "20px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(79, 70, 229, 0.08)",
    color: "#4f46e5",
    fontSize: "28px",
    marginBottom: "14px"
  },
  emptyTitle: {
    fontSize: "18px",
    fontWeight: 800,
    color: "#0f172a",
    marginBottom: "8px"
  },
  emptyText: {
    fontSize: "13px",
    lineHeight: 1.6,
    color: "#64748b",
    maxWidth: "460px",
    margin: "0 auto"
  }
};

export default function SentDocs() {

  const route = [{ url: "", name: "Task Management" }];
  const route1 = [{ url: "", name: "Contract Management" }];

  const usrType = localStorage.getItem("userType");

  const [appTasklist, setAppTasklist] = useState([]);
  const [sentDocs, setSentDocs] = useState([]);
  const [receivedDocs, setReceivedDocs] = useState([]);
  const [userDetails, setUserDetails] = useState(null);
  const [uploadDocShowHide, setUploadDocShowHide] = useState(false);
  const [wfTask, setWfTask] = useState([]);
  const [wfId, setWfId] = useState(0);

  const userDetailsSelector = useSelector(getLoggedUserDetails);
  const candidateAppsSelector = useSelector(getCandidateApps);
  const appTasksSelector = useSelector(getAppTasks);
  const appDocumentsSelector = useSelector(getAppDocuments);
  const reqDocumentsSelector = useSelector(getReqDocuments);
  const invDocumentsSelector = useSelector(getInvDocuments);
  const componentUserStatusSelector = useSelector(getComponentUserStatus);

  const [handleDownloadFile] = useDocDownload();

  const dispatch = useDispatch();
  const currentUser = getCurrentUser();

  useEffect(() => {
    dispatch(setCurrentPageTitle("Document History"));
    dispatch(setCurrentRoute(usrType === "candidate" ? route : route1))
  }, [])

  useEffect(() => {
    let usrId = currentUser.id;
    if (currentUser.linkUser)
      usrId = currentUser.linkUser;
    if (!userDetailsSelector && currentUser?.id > 0)
      dispatch(loadUserDetails(usrId));

    if (currentUser?.id > 0)
      dispatch(loadCandidateApps(usrId));
  }, []);

  useEffect(() => {
    setUserDetails(userDetailsSelector);
  }, [userDetailsSelector])

  useEffect(() => {
    if (candidateAppsSelector.length > 0 && currentUser?.id > 0) {
      const mainWorkflow = candidateAppsSelector.filter(x => x.workflowType === 1);
      let usrId = currentUser.id;
      if (currentUser.linkUser)
        usrId = currentUser.linkUser;
      if (mainWorkflow.length > 0) {
        const wf = mainWorkflow[0];
        setWfId(wf.oldWorkflowId);
        dispatch(loadAppRuningTasks(wf.oldWorkflowId, usrId));
        dispatch(loadAppDocuments(wf.oldWorkflowId, usrId));
        dispatch(loadInvoiceDocuments(usrId));
      }
    }
  }, [candidateAppsSelector]);

  useEffect(() => {
    if (appTasksSelector.length > 0) {
      const filterTasks = appTasksSelector.filter(x => x.taskType === 1 && x.wiid > 0);
      setAppTasklist(filterTasks);
      if (filterTasks.length > 0) {
        dispatch(loadReqDocuments(filterTasks[0].ciid));
      }
    }
  }, [appTasksSelector]);

  useEffect(() => {
    if (appDocumentsSelector.length > 0) {
      const appDocs = [...appDocumentsSelector];
      let receiveDoc = appDocs.filter(
        (x) =>
          x.type === "received"
          && x.uploadType !== "Invoice"
          // && x.uploadType !== "reporting" 
          // && x.uploadType !== "client" 
          // && x.uploadType !== "project" 
          // && x.docType !== "internal" 
          && x.sendStatus === true
          && x.deleteFlag !== true
      );
      let msgDocs = appDocs.filter(x => x.type === "received" && x.docType === "Messaging");
      receiveDoc = [...receiveDoc, ...msgDocs];
      let sentDocs = appDocs.filter(
        (x) =>
          x.type === "send"
          && x.uploadType !== "monthly_time_sheet"
          && x.uploadType !== "Invoice"
        // && x.docType !== "internal" 
        // && x.uploadType?.substring(0, 3) !== "ach" 
        // && x.uploadType?.substring(0, 9) !== "insurance"
      );

      if (reqDocumentsSelector.length > 0) {
        // reqDocumentsSelector.map(r => {
        //   sentDocs = sentDocs.filter(x => x.uploadType !== "req_" + r.id);
        // })
      }

      if (userDetails) {
        const subId = userDetails.subcontractor?.fuid;
        // receiveDoc = receiveDoc.filter(
        //   (x) =>
        //     x.uploadType !== `1_c2c_${subId}` &&
        //     x.uploadType !== `2_c2c_${subId}` &&
        //     x.uploadType !== `3_c2c_${subId}` &&
        //     x.uploadType !== `4_c2c_${subId}`
        // );
      }
      //console.log(receiveDoc)
      setReceivedDocs(receiveDoc);
      setSentDocs(sentDocs);

      let docItems = "";
      receiveDoc.map((x, key) => {
        if (key > 0) docItems += ",";
        docItems += x.id;
      })

      sentDocs.map((x, key) => {
        if (key > 0) docItems += ",";
        docItems += x.id;
      })
      let appCiid = 0;
      if (receiveDoc.length > 0)
        appCiid = receiveDoc[0].ciid;

      if (sentDocs.length > 0)
        appCiid = sentDocs[0].ciid;

      handleUpdateComponent(docItems, appCiid);

    }
  }, [reqDocumentsSelector, invDocumentsSelector])


  const handleUploadDocModalShowHide = (docs) => {
    if (docs === "save") {
      setUploadDocShowHide(false);
      let usrId = currentUser.id;
      if (currentUser.linkUser)
        usrId = currentUser.linkUser;
      dispatch(loadAppDocuments(wfId, usrId));
    }
    else {
      const reviewApp = appTasklist.filter((x) => x.registrationType === "WP");
      if (reviewApp.length > 0) {
        const lastReviewer = reviewApp[0];
        setWfTask([lastReviewer]);
      } else {
        getAlert("", "No reviewer found", "error");
      }
      setUploadDocShowHide(!uploadDocShowHide);
    }
  };

  const handleUpdateComponent = (compItems, ciid) => {
    const comp = componentUserStatusSelector.filter(x => x.compId === 2);
    let updCompItems = "";
    if (comp.length > 0) {
      updCompItems = comp[0].compItems;
    }
    const compObj = {
      compName: "Document",
      performerId: currentUser.id,
      compItems: compItems,
      ciid: ciid,
      wiid: 0,
      iiid: 0,
      compId: 2
    }
    if (compItems !== "" && compItems !== updCompItems) {
      dispatch(UpdateComponentsUserStatus(compObj));
    }
  }

  const getDocType = (type) => {
    if (type === "sharing")
      return "Contract";
    else if (type?.substring(0, 3) === "ach")
      return "ACH Void Check";
    else if (type?.substring(0, 9) === "insurance")
      return "COI Insurance";
    else if (type?.substring(0, 3) === "req" || type?.includes("_c2c_"))
      return "Required";
    else return type;
  }

  const totalDocuments = sentDocs.length + receivedDocs.length;
  const totalSentDocuments = sentDocs.length;
  const totalReceivedDocuments = receivedDocs.length;

  return (
    <React.Fragment>
      <div className="col-md-12" style={SENT_DOCS_STYLES.pageWrap}>
        <div style={SENT_DOCS_STYLES.shell}>
          <div style={SENT_DOCS_STYLES.hero}>
            <div style={SENT_DOCS_STYLES.heroRow}>
              <div style={SENT_DOCS_STYLES.titleWrap}>
                <div style={SENT_DOCS_STYLES.titleIcon}>
                  <i className="fa fa-folder-open-o" aria-hidden="true"></i>
                </div>
                <div style={SENT_DOCS_STYLES.titleCopy}>
                  <h1 style={SENT_DOCS_STYLES.title}>Document History</h1>
                  <p style={SENT_DOCS_STYLES.subtitle}>
                    Review every file exchanged in your workflow, download the latest copies, and track what was sent versus what was received.
                  </p>
                </div>
              </div>
            </div>
            <div style={SENT_DOCS_STYLES.statsRow}>
              <div style={SENT_DOCS_STYLES.statCard}>
                <div style={SENT_DOCS_STYLES.statLabel}>Total Documents</div>
                <div style={SENT_DOCS_STYLES.statValue}>{totalDocuments}</div>
                <div style={SENT_DOCS_STYLES.statHint}>All shared files currently tracked in your workflow.</div>
              </div>
              <div style={SENT_DOCS_STYLES.statCard}>
                <div style={SENT_DOCS_STYLES.statLabel}>Sent</div>
                <div style={SENT_DOCS_STYLES.statValue}>{totalSentDocuments}</div>
                <div style={SENT_DOCS_STYLES.statHint}>Documents you or your side have sent for review.</div>
              </div>
              <div style={SENT_DOCS_STYLES.statCard}>
                <div style={SENT_DOCS_STYLES.statLabel}>Received</div>
                <div style={SENT_DOCS_STYLES.statValue}>{totalReceivedDocuments}</div>
                <div style={SENT_DOCS_STYLES.statHint}>Files received back from reviewers or related workflows.</div>
              </div>
            </div>
          </div>
          <div style={SENT_DOCS_STYLES.body}>
            <div style={SENT_DOCS_STYLES.sectionCard}>
              <div style={SENT_DOCS_STYLES.sectionHeader}>
                <div style={SENT_DOCS_STYLES.sectionTitleWrap}>
                  <div style={SENT_DOCS_STYLES.sectionIcon}>
                    <i className="fa fa-upload" aria-hidden="true"></i>
                  </div>
                  <div>
                    <div style={SENT_DOCS_STYLES.sectionTitle}>Sent Documents</div>
                    <div style={SENT_DOCS_STYLES.sectionMeta}>Files sent out from your workflow.</div>
                  </div>
                </div>
                <div style={SENT_DOCS_STYLES.sectionBadge}>{sentDocs.length}</div>
              </div>
              <div className="table-responsive">
                <table className="table align-items-center table-hover mb-0" style={SENT_DOCS_STYLES.table}>
                  <thead>
                    <tr>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}></th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Date & Time Sent</th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Name</th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Type</th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Size</th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sentDocs.length > 0 ? sentDocs.map((item, key) => (
                      <tr key={item.id || `sent-${key}`}>
                        <td style={SENT_DOCS_STYLES.cell}>
                          <span style={SENT_DOCS_STYLES.serialBadge}>{key + 1}</span>
                        </td>
                        <td style={SENT_DOCS_STYLES.cell}>
                          <div style={SENT_DOCS_STYLES.dateText}>
                            <Moment format="MMM Do YYYY, h:mm a">
                              {item.createdDateTime}
                            </Moment>
                          </div>
                        </td>
                        <td style={SENT_DOCS_STYLES.cell}>
                          <button
                            style={SENT_DOCS_STYLES.fileButton}
                            title="Download Document"
                            onClick={() => handleDownloadFile(item)}
                          >
                            {item.documentName}
                          </button>
                          {item.docModifiedName && (
                            <div style={SENT_DOCS_STYLES.fileSubtext}>{item.docModifiedName}</div>
                          )}
                        </td>
                        <td style={SENT_DOCS_STYLES.cell}>
                          <span style={SENT_DOCS_STYLES.typeBadge}>{getDocType(item.uploadType)}</span>
                        </td>
                        <td style={SENT_DOCS_STYLES.cell}>{item.docSize || "-"}</td>
                        <td style={SENT_DOCS_STYLES.cell}>
                          <div style={SENT_DOCS_STYLES.notesText}>{item.notes || "No notes added."}</div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="6" style={{ ...SENT_DOCS_STYLES.cell, padding: 0 }}>
                          <div style={SENT_DOCS_STYLES.emptyState}>
                            <div style={SENT_DOCS_STYLES.emptyIcon}>
                              <i className="fa fa-upload" aria-hidden="true"></i>
                            </div>
                            <div style={SENT_DOCS_STYLES.emptyTitle}>No sent documents yet</div>
                            <div style={SENT_DOCS_STYLES.emptyText}>
                              Documents you send through the workflow will appear here for quick download and review.
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={SENT_DOCS_STYLES.sectionCard}>
              <div style={SENT_DOCS_STYLES.sectionHeader}>
                <div style={SENT_DOCS_STYLES.sectionTitleWrap}>
                  <div style={SENT_DOCS_STYLES.sectionIcon}>
                    <i className="fa fa-download" aria-hidden="true"></i>
                  </div>
                  <div>
                    <div style={SENT_DOCS_STYLES.sectionTitle}>Received Documents</div>
                    <div style={SENT_DOCS_STYLES.sectionMeta}>Files returned or shared back into your workflow.</div>
                  </div>
                </div>
                <div style={SENT_DOCS_STYLES.sectionBadge}>{receivedDocs.length}</div>
              </div>
              <div className="table-responsive">
                <table className="table align-items-center table-hover mb-0" style={SENT_DOCS_STYLES.table}>
                  <thead>
                    <tr>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}></th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Date & Time Received</th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Name</th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Type</th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Size</th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receivedDocs.length > 0 ? receivedDocs.map((item, key) => (
                      <tr key={item.id || `received-${key}`}>
                        <td style={SENT_DOCS_STYLES.cell}>
                          <span style={SENT_DOCS_STYLES.serialBadge}>{key + 1}</span>
                        </td>
                        <td style={SENT_DOCS_STYLES.cell}>
                          <div style={SENT_DOCS_STYLES.dateText}>
                            <Moment format="MMM Do YYYY, h:mm a">
                              {item.createdDateTime}
                            </Moment>
                          </div>
                        </td>
                        <td style={SENT_DOCS_STYLES.cell}>
                          <button
                            style={SENT_DOCS_STYLES.fileButton}
                            title="Download Document"
                            onClick={() => handleDownloadFile(item)}
                          >
                            {item.documentName}
                          </button>
                          {item.docModifiedName && (
                            <div style={SENT_DOCS_STYLES.fileSubtext}>{item.docModifiedName}</div>
                          )}
                        </td>
                        <td style={SENT_DOCS_STYLES.cell}>
                          <span style={SENT_DOCS_STYLES.typeBadge}>{getDocType(item.docType)}</span>
                        </td>
                        <td style={SENT_DOCS_STYLES.cell}>{item.docSize || "-"}</td>
                        <td style={SENT_DOCS_STYLES.cell}>
                          <div style={SENT_DOCS_STYLES.notesText}>{item.notes || "No notes added."}</div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="6" style={{ ...SENT_DOCS_STYLES.cell, padding: 0 }}>
                          <div style={SENT_DOCS_STYLES.emptyState}>
                            <div style={SENT_DOCS_STYLES.emptyIcon}>
                              <i className="fa fa-download" aria-hidden="true"></i>
                            </div>
                            <div style={SENT_DOCS_STYLES.emptyTitle}>No received documents yet</div>
                            <div style={SENT_DOCS_STYLES.emptyText}>
                              Received documents will show up here once files are shared back into your workflow.
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {uploadDocShowHide &&

        <UploadDocPopup
          uploadDocShowHide={uploadDocShowHide}
          handleUploadDocModalShowHide={handleUploadDocModalShowHide}
          runningApplication={wfTask}
        ></UploadDocPopup>}
    </React.Fragment>
  );
}
